import { routerMiddleware } from 'connected-react-router';
import { createBrowserHistory, createMemoryHistory, History, MemoryHistory } from 'history';
import { compose, Middleware } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { SagaMiddleware } from 'redux-saga';
import { createStateSyncMiddleware } from 'redux-state-sync';
import { ACTIONS_TO_SYNC as CART_ACTIONS_TO_SYNC } from 'b2b-apps/shop/cart/redux/cart/actionTypes';
import { ACTIONS_TO_SYNC as CART_STATUS_ACTIONS_TO_SYNC } from 'b2b-apps/shop/cart/redux/cartStatus/actionTypes';
import { Action, Dispatch, Store } from 'b2b-apps/shop/common/redux/types';
import { isServer } from 'b2b-apps/shop/common/utils/env';
import { SYNC_CONTEXT } from 'b2b-apps/shop/context/redux/actionTypes';
import { ACTIONS_TO_SYNC as I18N_ACTIONS_TO_SYNC } from 'b2b-apps/shop/i18n/redux/actionTypes';

const STATE_SYNC_MIDDLEWARE_CONFIG = {
  whitelist: [
    SYNC_CONTEXT,
    ...CART_ACTIONS_TO_SYNC,
    ...CART_STATUS_ACTIONS_TO_SYNC,
    ...I18N_ACTIONS_TO_SYNC,
  ],
};

type MiddlewareType = Middleware<Store, Action, Dispatch>;

type StoreConfigType = {
  history: History | MemoryHistory;
  composeEnhancers: any;
  middlewares: MiddlewareType[];
};

const getClientStoreConfig = (
  middlewares: MiddlewareType[],
): StoreConfigType => {
  const history = createBrowserHistory({});
  const composeEnhancers = process.env.NODE_ENV === 'development'
    ? composeWithDevTools({
      maxAge: 100,
      trace: true,
    })
    : compose;

  return {
    history,
    composeEnhancers,
    middlewares: [
      routerMiddleware(history),
      createStateSyncMiddleware(STATE_SYNC_MIDDLEWARE_CONFIG),
      ...middlewares,
    ],
  };
};

const getServerStoreConfig = (
  middlewares: MiddlewareType[],
  url: string,
): StoreConfigType => {
  const history = createMemoryHistory({
    initialEntries: [url],
  });

  return {
    history,
    composeEnhancers: compose,
    middlewares: [routerMiddleware(history), ...middlewares],
  };
};

const getStoreConfig = (
  url = '/',
  sagaMiddleware: SagaMiddleware,
): StoreConfigType => {
  const middlewares = [sagaMiddleware];

  if (isServer) {
    return getServerStoreConfig(middlewares, url);
  }

  return getClientStoreConfig(middlewares);
};

export type { MiddlewareType, StoreConfigType };
export { getStoreConfig };
